import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { IconCheckmark2 } from 'central-icons/IconCheckmark2';
import type { ReactNode } from 'react';

import {
  RailModalBody,
  RailModalCloseButton,
  RailModalContent,
  RailModalDivider,
  RailModalFooter,
  RailModalFooterSpacer,
  RailModalHeader,
  RailModalPane,
  RailModalRail,
  RailModalRailGroup,
  RailModalRailItem,
  Button as UiButton,
} from '@revelio/ui';

import {
  type VideoData,
  videoMetadata,
} from '../../shared/ui/tutorial/Tutorial';
import styles from './tutorial-modal.module.css';

export interface TutorialVideoGroup {
  label: string;
  videos: VideoData[];
}

const THEMED_GROUPS: ReadonlyArray<{ label: string; videoIds: string[] }> = [
  { label: 'Analytics', videoIds: ['0', '11', '1', '2', '3', '4', '5'] },
  { label: 'Discovery', videoIds: ['6', '7'] },
  { label: 'Custom Reports & Datasets', videoIds: ['8', '10'] },
];

export const buildTutorialModalGroups = (): TutorialVideoGroup[] => {
  const allVideos = videoMetadata.flatMap((category) => category.videos);
  const videosById = new Map(allVideos.map((video) => [video.id, video]));
  const mappedIds = new Set(THEMED_GROUPS.flatMap((group) => group.videoIds));

  const groups = THEMED_GROUPS.map((group) => ({
    label: group.label,
    videos: group.videoIds.flatMap((id) => videosById.get(id) ?? []),
  }));

  const unmappedVideos = allVideos.filter((video) => !mappedIds.has(video.id));
  const lastGroup = groups[groups.length - 1];
  if (unmappedVideos.length > 0 && lastGroup) {
    lastGroup.videos.push(...unmappedVideos);
  }

  return groups.filter((group) => group.videos.length > 0);
};

interface TutorialModalContentProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseComplete?: () => void;
  closeOnOverlayClick?: boolean;
  groups: TutorialVideoGroup[];
  activeVideo: VideoData | null;
  watchedIds: ReadonlySet<string>;
  onSelectVideo: (videoId: string) => void;
  onOpenSection: (video: VideoData) => void;
  player: ReactNode;
  descriptionSuffix?: ReactNode;
}

export const TutorialModalContent = ({
  isOpen,
  onClose,
  onCloseComplete,
  closeOnOverlayClick,
  groups,
  activeVideo,
  watchedIds,
  onSelectVideo,
  onOpenSection,
  player,
  descriptionSuffix,
}: TutorialModalContentProps) => {
  const flatVideos = groups.flatMap((group) => group.videos);
  const activeIndex = flatVideos.findIndex(
    (video) => video.id === activeVideo?.id
  );
  const previousVideo = activeIndex > 0 ? flatVideos[activeIndex - 1] : null;
  const nextVideo =
    activeIndex >= 0 && activeIndex < flatVideos.length - 1
      ? flatVideos[activeIndex + 1]
      : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      onCloseComplete={onCloseComplete}
      closeOnOverlayClick={closeOnOverlayClick}
      isCentered
    >
      <ModalOverlay className={styles.overlay} />
      <RailModalContent asChild>
        <ModalContent className={styles.content}>
          <RailModalHeader asChild>
            <ModalHeader>Tutorials</ModalHeader>
          </RailModalHeader>
          <RailModalCloseButton asChild>
            <ModalCloseButton data-testid="tutorial-modal-close-btn" />
          </RailModalCloseButton>
          <RailModalDivider />
          <RailModalBody asChild>
            <ModalBody>
              <RailModalRail aria-label="Tutorial sections">
                {groups.map((group) => (
                  <RailModalRailGroup key={group.label} label={group.label}>
                    {group.videos.map((video) => (
                      <RailModalRailItem
                        key={video.id}
                        isActive={video.id === activeVideo?.id}
                        trailing={
                          watchedIds.has(video.id) && (
                            <IconCheckmark2 size={16} />
                          )
                        }
                        onClick={() => onSelectVideo(video.id)}
                      >
                        {video.name}
                      </RailModalRailItem>
                    ))}
                  </RailModalRailGroup>
                ))}
              </RailModalRail>
              <RailModalPane>
                <div
                  className={styles.playerFrame}
                  data-testid="tutorial-video-player"
                >
                  {player}
                </div>
                <div className={styles.paneText}>
                  <p className={styles.paneTitle}>{activeVideo?.name}</p>
                  <p className={styles.paneDescription}>
                    {activeVideo?.description}
                    {descriptionSuffix}
                  </p>
                </div>
              </RailModalPane>
            </ModalBody>
          </RailModalBody>
          <RailModalDivider />
          <RailModalFooter>
            {previousVideo && (
              <UiButton
                variant="ghost"
                className={styles.ctaGhost}
                onClick={() => onSelectVideo(previousVideo.id)}
              >
                Back
              </UiButton>
            )}
            {nextVideo && (
              <UiButton
                variant="ghost"
                className={styles.ctaGhost}
                onClick={() => onSelectVideo(nextVideo.id)}
              >
                Next
              </UiButton>
            )}
            <RailModalFooterSpacer />
            {activeVideo && activeVideo.paths.length > 0 && (
              <UiButton
                color="primary"
                className={styles.ctaPrimary}
                onClick={() => onOpenSection(activeVideo)}
              >
                Open {activeVideo.name.toLowerCase()}
              </UiButton>
            )}
          </RailModalFooter>
        </ModalContent>
      </RailModalContent>
    </Modal>
  );
};
